// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { WellFormedError } from '@/api/types';
import {
  CreateUsefulLifeRevisionParameters,
  createUsefulLifeRevision,
} from '@/asset/api/create-useful-life-revision';
import { UsefulLifeRevision } from '@/asset/api/types';
import { UsefulLifeRevisionDialog } from '@/asset/components/useful-life-revision-dialog';
import { UsefulLifeRevisionTable } from '@/asset/components/useful-life-revision-table';
import { SupportSnackbar } from '@/components/support-snackbar';
import { AddActionIcon } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Alert, Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

interface Props {
  assetKey: string;
  revisions: UsefulLifeRevision[];
}

export function UsefulLifeRevisionSection(props: Props) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const createDialog = useDisclosure();
  const [apiError, setAPIError] = useState<WellFormedError | null>(null);

  const invalidateAssetQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['asset', props.assetKey],
      }),
      queryClient.invalidateQueries({
        queryKey: ['asset', props.assetKey, 'audit'],
      }),
    ]);
  };

  const createMutation = useMutation({
    mutationFn: (parameters: CreateUsefulLifeRevisionParameters) =>
      createUsefulLifeRevision(props.assetKey, parameters),
    onSuccess: async () => {
      setAPIError(null);
      await invalidateAssetQueries();
      createDialog.close();
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      setAPIError(apiError);

      const curated = getUserFacingErrorMessage(apiError);
      if (
        curated === SUPPORT_FALLBACK_ERROR_MESSAGE &&
        !apiError.sentToSentry
      ) {
        captureException(error);
      }
    },
  });

  const handleCloseSnackbar = () => {
    setAPIError(null);
    createMutation.reset();
  };

  const handleCloseDialog = () => {
    setAPIError(null);
    createMutation.reset();
    createDialog.close();
  };

  const handleSubmit = (parameters: CreateUsefulLifeRevisionParameters) => {
    setAPIError(null);
    createMutation.reset();
    createMutation.mutate(parameters);
  };

  return (
    <Box
      sx={{
        borderTop: `1px solid ${theme.palette.divider}`,
        mt: 4,
        pt: 3,
      }}
    >
      <SupportSnackbar
        isOpen={apiError !== null}
        onClose={handleCloseSnackbar}
        message="Unable to record useful life revision."
      />
      <UsefulLifeRevisionDialog
        isOpen={createDialog.isOpen}
        isPending={createMutation.isPending}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
      />
      <Stack
        alignItems={{ sm: 'center', xs: 'flex-start' }}
        direction={{ sm: 'row', xs: 'column' }}
        justifyContent="space-between"
        mb={2}
        spacing={2}
      >
        <Typography variant="h6">Useful life revisions</Typography>
        <Button
          startIcon={<AddActionIcon />}
          variant="contained"
          onClick={createDialog.open}
        >
          Add revision
        </Button>
      </Stack>
      <Alert severity="info" sx={{ mb: 2 }}>
        Add useful life revisions to record prospective changes that{' '}
        <strong>update</strong> the asset's useful life. To restate an asset's{' '}
        <strong>entire</strong> useful life retroactively, update the asset's
        original useful life.
      </Alert>
      <UsefulLifeRevisionTable revisions={props.revisions} />
    </Box>
  );
}
